import { cn } from '@/lib/cn';
import { priceParts, priceSub } from '@/lib/format';

/* ────────────────────────────────────────────────────────────────────────────
   One canonical way to render a token price.

   Launch prices run from ~1e-3 down past 1e-10. Switching between decimals and
   exponents by magnitude — which is what a naive formatter does — puts
   `0.000002103` next to `3.62e-7` in the same column and reads as a bug. This
   collapses the leading zero run into a subscript count instead, so every price
   in the app has the same shape and the same width class.
   ──────────────────────────────────────────────────────────────────────────── */

export interface PriceProps {
  value: number;
  /** Appended in muted text, e.g. "ETH". */
  unit?: string;
  className?: string;
  /** Subscript size relative to the digits. */
  subClassName?: string;
}

export function Price({ value, unit, className, subClassName }: PriceProps) {
  const { lead, zeros, digits, negative } = priceParts(value);

  return (
    <span
      className={cn('font-mono tabular-nums whitespace-nowrap', className)}
      // Screen readers get the flat form; the subscript would be read as a digit.
      aria-label={unit ? `${priceSub(value)} ${unit}` : priceSub(value)}
    >
      <span aria-hidden="true">
        {negative ? '-' : ''}
        {lead}
        {zeros > 0 ? (
          <sub className={cn('align-baseline text-[0.72em] text-muted', subClassName)}>{zeros}</sub>
        ) : null}
        {digits}
      </span>
      {unit ? <span className="ml-1 text-muted">{unit}</span> : null}
    </span>
  );
}
